import React, { useEffect, useMemo, useState } from "react";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";

import ProjectTable from "pages/components/ProjectTable/ProjectTable";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

import { createAutocompleteFilter } from "pages/utils/autocompleteFilter";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

/* ------------------- Helpers ------------------- */

const fmtNum = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  return Number.isFinite(n) ? n : "";
};

const fmtMoney = (v) => {
  if (v === null || v === undefined || v === "") return "0,00";
  const n = Number(v);
  if (!Number.isFinite(n)) return "0,00";
  return n.toLocaleString("pt-PT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const calcTotalLinha = (q, pu) => {
  const nq = Number(q);
  const npu = Number(pu);
  if (!Number.isFinite(nq) || !Number.isFinite(npu)) return 0;
  return nq * npu;
};

/* ========================================================= */

export default function DialogEquipamentoEventoLinhas({ open, onClose, evento, onChanged }) {
  const { showAlert } = useAlert();
  const { temPermissao } = useAuth();

  /* ===============================
     🔒 Congelar ID do evento
  =============================== */

  const [idEvento, setIdEvento] = useState(0);
  const [tituloEvento, setTituloEvento] = useState("");

  useEffect(() => {
    if (!open) return;

    const id = Number(evento?.id || 0);
    setIdEvento(id);
    setTituloEvento(evento?.titulo ? String(evento.titulo) : "");
  }, [open, evento]);

  /* =============================== */

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);

  const TIPOS_LINHA = ["PEÇA", "SERVIÇO", "OUTRO"];

  const emptyForm = {
    id: 0,
    id_evento: 0,
    descritivo: "",
    quantidade: "",
    preco_unitario: "",
    tipo_linha: "PEÇA",
  };

  const [openEdit, setOpenEdit] = useState(false);
  const [editando, setEditando] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });

  // confirmar apagar
  const [openConfirmDel, setOpenConfirmDel] = useState(false);
  const [linhaParaApagar, setLinhaParaApagar] = useState(null);

  /* ===============================
     Carregar linhas
  =============================== */

  const carregar = async () => {
    if (!idEvento) {
      setData([]);
      return;
    }

    setLoading(true);

    const [payload, err] = await handleApi(
      api.post("Equipamentos/SelEquipamentoEventoLinhas", {
        id_evento: idEvento,
      })
    );

    setLoading(false);

    if (err) return showAlert(err, "error");

    const arr = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];

    setData(arr);
  };

  useEffect(() => {
    if (!open) return;
    carregar();
    // eslint-disable-next-line
  }, [open, idEvento]);

  /* ===============================
     Abrir/Fechar edição
  =============================== */

  const handleOpenNovo = () => {
    if (!temPermissao("Menu Equipamentos", "w")) return;
    if (!idEvento) return showAlert("Evento inválido.", "warning");

    setEditando(false);
    setForm({
      ...emptyForm,
      id_evento: idEvento,
      tipo_linha: "PEÇA",
    });

    setOpenEdit(true);
  };

  const handleOpenEditar = (ln) => {
    if (!temPermissao("Menu Equipamentos", "u")) return;

    setEditando(true);
    setForm({
      ...emptyForm,
      ...ln,
      id: ln.id,
      id_evento: ln.id_evento ?? idEvento,
      descritivo: (ln.descritivo ?? "").toUpperCase(),
      quantidade: ln.quantidade ?? "",
      preco_unitario: ln.preco_unitario ?? "",
      tipo_linha: (ln.tipo_linha ?? "PEÇA").toUpperCase(),
    });

    setOpenEdit(true);
  };

  const handleCloseEdit = () => {
    setOpenEdit(false);
    setEditando(false);
    setForm({ ...emptyForm });
  };

  const handleCloseAll = () => {
    handleCloseEdit();

    setOpenConfirmDel(false);
    setLinhaParaApagar(null);
    setData([]);

    if (typeof onClose === "function") onClose();
  };

  /* ===============================
     Guardar linha
  =============================== */

  const handleSave = async () => {
    const idEv = Number(form.id_evento || 0);
    if (idEv <= 0) return showAlert("Evento obrigatório.", "warning");

    const desc = (form.descritivo ?? "").trim().toUpperCase();
    if (!desc) return showAlert("Descritivo obrigatório.", "warning");

    const qtd = Number(form.quantidade);
    const pu = Number(form.preco_unitario);

    if (!Number.isFinite(qtd) || qtd <= 0) return showAlert("Quantidade inválida.", "warning");

    if (!Number.isFinite(pu) || pu < 0) return showAlert("Preço unitário inválido.", "warning");

    setLoading(true);

    const body = {
      ...form,
      id_evento: idEv,
      descritivo: desc,
      tipo_linha: (form.tipo_linha ?? "OUTRO").toUpperCase(),
      quantidade: qtd,
      preco_unitario: pu,
    };

    const endpoint = editando
      ? "Equipamentos/UpdEquipamentoEventoLinha"
      : "Equipamentos/InsEquipamentoEventoLinha";

    const [_, err, meta] = await handleApi(api.post(endpoint, body));

    setLoading(false);

    if (err) return showAlert(err, "error");

    showAlert(meta?.message ?? "OK", meta?.severity ?? "success");

    await carregar();
    handleCloseEdit();

    if (typeof onChanged === "function") onChanged();
  };

  /* ===============================
     Apagar linha
  =============================== */

  const apagar = async (ln) => {
    if (!temPermissao("Menu Equipamentos", "d")) return;
    if (!ln?.id) return;

    setLoading(true);

    const [_, err, meta] = await handleApi(
      api.post("Equipamentos/DelEquipamentoEventoLinha", {
        id: ln.id,
        id_evento: idEvento,
      })
    );

    setLoading(false);

    if (err) return showAlert(err, "error");

    showAlert(meta?.message ?? "OK", meta?.severity ?? "success");

    await carregar();

    if (typeof onChanged === "function") onChanged();
  };

  const confirmarApagar = async () => {
    if (!linhaParaApagar?.id) return;

    await apagar(linhaParaApagar);

    setOpenConfirmDel(false);
    setLinhaParaApagar(null);
  };

  /* ===============================
     Tabela
  =============================== */

  const rowActions = useMemo(
    () => [
      {
        id: "editar",
        icon: <EditIcon fontSize="small" />,
        tooltip: "Editar",
        color: "warning",
        visible: () => temPermissao("Menu Equipamentos", "u"),
        onClick: (ln) => handleOpenEditar(ln),
      },
      {
        id: "apagar",
        icon: <DeleteIcon fontSize="small" />,
        tooltip: "Apagar",
        color: "error",
        visible: () => temPermissao("Menu Equipamentos", "d"),
        onClick: (ln) => {
          setLinhaParaApagar(ln);
          setOpenConfirmDel(true);
        },
      },
    ],
    [temPermissao]
  );

  const columns = useMemo(
    () => [
      { Header: "Tipo", accessor: "tipo_linha" },
      { Header: "Descritivo", accessor: "descritivo" },
      {
        Header: "Qtd",
        accessor: "quantidade",
        Cell: ({ value }) => fmtNum(value),
      },
      {
        Header: "Preço",
        accessor: "preco_unitario",
        Cell: ({ value }) => fmtMoney(value),
      },
      {
        Header: "Total",
        id: "total",
        disableFilters: true,
        Cell: ({ row }) =>
          fmtMoney(calcTotalLinha(row.original?.quantidade, row.original?.preco_unitario)),
      },
    ],
    []
  );

  const subtotalLinhas = useMemo(() => {
    return (data || []).reduce(
      (acc, ln) => acc + calcTotalLinha(ln?.quantidade, ln?.preco_unitario),
      0
    );
  }, [data]);

  /* ===============================
     Render
  =============================== */

  return (
    <>
      <Dialog open={open} onClose={handleCloseAll} maxWidth="lg" fullWidth>
        <DialogTitle>Linhas do Evento — {tituloEvento || `Evento #${idEvento}`}</DialogTitle>

        <DialogContent>
          {loading && (
            <MDBox py={2} display="flex" justifyContent="center">
              <CircularProgress />
            </MDBox>
          )}

          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
            mb={1.5}
            mt={0.5}
          >
            <MDTypography variant="caption" sx={{ color: "#7b809a" }}>
              {idEvento ? `ID Evento: ${idEvento}` : ""}
            </MDTypography>

            <MDButton
              color="success"
              disabled={!temPermissao("Menu Equipamentos", "w")}
              onClick={handleOpenNovo}
            >
              Nova Linha
            </MDButton>
          </MDBox>

          <ProjectTable
            tableKey="equipamento-evento-linhas"
            version="v1"
            columns={columns}
            data={data}
            loading={loading}
            rowActions={rowActions}
            emptyMessage="Sem linhas para este evento."
            minWidth={900}
            maxHeight="55vh"
            showKeepFilters
          />

          <MDBox mt={1.5} display="flex" justifyContent="flex-end">
            <MDTypography variant="button">
              Subtotal linhas: {fmtMoney(subtotalLinhas)}
            </MDTypography>
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton onClick={handleCloseAll} color="secondary">
            Fechar
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Dialog Novo/Editar */}
      <Dialog open={openEdit} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
        <DialogTitle>{editando ? "Editar Linha" : "Nova Linha"}</DialogTitle>

        <DialogContent>
          <MDBox display="grid" gap={1.25} mt={0.75}>
            <Autocomplete
              options={TIPOS_LINHA}
              filterOptions={createAutocompleteFilter((opt) => opt || "")}
              value={
                TIPOS_LINHA.includes((form.tipo_linha || "").toUpperCase())
                  ? form.tipo_linha.toUpperCase()
                  : "OUTRO"
              }
              onChange={(_, v) =>
                setForm((p) => ({ ...p, tipo_linha: (v || "OUTRO").toUpperCase() }))
              }
              disableClearable
              renderInput={(params) => <TextField {...params} size="small" fullWidth />}
            />

            <MDInput
              label="Descritivo *"
              value={form.descritivo || ""}
              onChange={(e) =>
                setForm((p) => ({ ...p, descritivo: (e.target.value ?? "").toUpperCase() }))
              }
              fullWidth
            />

            <MDBox
              display="grid"
              gap={1.25}
              sx={{
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "1fr 1fr",
                },
              }}
            >
              <MDInput
                label="Quantidade *"
                type="number"
                value={form.quantidade ?? ""}
                onChange={(e) => setForm((p) => ({ ...p, quantidade: e.target.value }))}
                fullWidth
              />

              <MDInput
                label="Preço unitário *"
                type="number"
                value={form.preco_unitario ?? ""}
                onChange={(e) => setForm((p) => ({ ...p, preco_unitario: e.target.value }))}
                fullWidth
              />
            </MDBox>

            <TextField
              size="small"
              fullWidth
              disabled
              value={calcTotalLinha(form.quantidade, form.preco_unitario)}
            />
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton onClick={handleCloseEdit} color="secondary">
            Cancelar
          </MDButton>

          <MDButton
            onClick={handleSave}
            color="success"
            disabled={
              editando
                ? !temPermissao("Menu Equipamentos", "u")
                : !temPermissao("Menu Equipamentos", "w")
            }
          >
            {editando ? "Guardar" : "Inserir"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Confirmar apagar */}
      <Dialog
        open={openConfirmDel}
        onClose={() => {
          setOpenConfirmDel(false);
          setLinhaParaApagar(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirmar eliminação</DialogTitle>

        <DialogContent>
          <MDTypography variant="body2">Apagar esta linha?</MDTypography>

          {linhaParaApagar?.descritivo && (
            <MDTypography variant="button" sx={{ display: "block", mt: 1 }}>
              {String(linhaParaApagar.descritivo)}
            </MDTypography>
          )}
        </DialogContent>

        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenConfirmDel(false);
              setLinhaParaApagar(null);
            }}
            color="secondary"
          >
            Cancelar
          </MDButton>

          <MDButton onClick={confirmarApagar} color="error">
            Apagar
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
